/**
 * Town-level programmatic SEO data.
 *
 * Each `TownData` powers a /locations/:stateSlug/:townSlug page and its four
 * service variants. Towns roll up to a parent county/region location so we
 * can cross-link the tiers and preserve every existing URL.
 *
 * Town slugs are unique within a state (`stateSlug` disambiguates), which
 * means "Weston" can exist in both CT and MA without collision.
 */

import type { LocationData } from "./locations";
import { getLocationBySlug } from "./locations";

export type DeerPressure = "low" | "moderate" | "high" | "very-high";

export interface TownData {
  slug: string;
  name: string;
  state: string; // e.g. "CT"
  stateName: string; // e.g. "Connecticut"
  stateSlug: string; // e.g. "connecticut"
  parentRegionSlug: string; // slug in locations.ts
  lat: number;
  lng: number;
  blurb: string;
  lotProfile: string;
  deerPressure: DeerPressure;
  notableAttributes: string[];
}

export interface StateHubData {
  slug: string;
  name: string;
  abbr: string;
  blurb: string;
}

export const stateHubs: StateHubData[] = [
  {
    slug: "connecticut",
    name: "Connecticut",
    abbr: "CT",
    blurb:
      "From Fairfield County estate lots to the wooded backroads of Litchfield, Connecticut homeowners choose our nearly-invisible non-electric fencing to contain dogs, keep deer out of gardens, and pass HOA review without a fight.",
  },
  {
    slug: "new-york",
    name: "New York",
    abbr: "NY",
    blurb:
      "We serve New York from the horse farms of northern Westchester up through the Hudson Valley, Saratoga, and out to the East End of Long Island. Every install is engineered for the local deer pressure, lot size, and terrain.",
  },
  {
    slug: "massachusetts",
    name: "Massachusetts",
    abbr: "MA",
    blurb:
      "MetroWest, the Berkshires, Cape Cod, and the North & South Shores — we install non-electric dog, deer, and garden fencing across Massachusetts, tuned for New England winters and Bay State conservation-commission review.",
  },
];

// Reserved for O(1) route disambiguation (see App.tsx).
export const stateSlugs = new Set(stateHubs.map((s) => s.slug));

// Set of slugs used by county/region pages we want to keep out of the town tier.
// This is a safety net; parentRegionSlug references locations.ts directly.
export const towns: TownData[] = [
  // ================= CONNECTICUT — Fairfield County =================
  {
    slug: "greenwich",
    name: "Greenwich",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.0262,
    lng: -73.6282,
    blurb:
      "Greenwich estates from Belle Haven to backcountry demand fencing that disappears against mature landscaping yet performs against real deer and dog pressure. Our non-electric systems clear ARB review and hold up on lots that regularly cross an acre.",
    lotProfile:
      "Estate lots 1–8 acres common in mid-country and backcountry, with dense tree cover and stone walls that our poly system integrates around without disturbance.",
    deerPressure: "very-high",
    notableAttributes: [
      "Backcountry deer corridors along the Byram and Mianus watersheds",
      "Strict ARB and neighborhood association review",
      "Estate perimeters often 1,500–5,000 linear feet",
    ],
  },
  {
    slug: "new-canaan",
    name: "New Canaan",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.1465,
    lng: -73.4954,
    blurb:
      "New Canaan properties combine mid-century modern architecture with wooded lots that see heavy deer traffic. Our low-visual-impact fencing keeps the sightlines clean while actually containing dogs and excluding deer from ornamental beds.",
    lotProfile:
      "Two- and three-acre wooded parcels with rolling ledge, most requiring careful post-setting around tree roots and boulders.",
    deerPressure: "very-high",
    notableAttributes: ["Iconic modernist homes", "Tick-heavy deer pressure", "Heavy tree canopy"],
  },
  {
    slug: "darien",
    name: "Darien",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.0787,
    lng: -73.4695,
    blurb:
      "Darien homeowners want a fence their neighbors won't notice from the street but their dogs can't get past. Our black poly and metal systems fade into hedgerows while holding a boundary that actually works.",
    lotProfile:
      "Half-acre to two-acre suburban lots, often with mature hedgerows we can integrate into the fence line.",
    deerPressure: "high",
    notableAttributes: ["Coastal HOA and neighborhood association culture", "Shoreline salt exposure"],
  },
  {
    slug: "westport",
    name: "Westport",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.1415,
    lng: -73.3579,
    blurb:
      "Westport's mix of shoreline properties and inland wooded lots calls for fences that survive Long Island Sound wind while disappearing into designed landscapes. We install year-round for Compo Beach cottages and Greens Farms estates alike.",
    lotProfile:
      "Half-acre coastal lots up to multi-acre inland estates north of the Post Road.",
    deerPressure: "high",
    notableAttributes: ["Coastal salt spray", "Design-forward architecture"],
  },
  {
    slug: "ridgefield",
    name: "Ridgefield",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.2815,
    lng: -73.4979,
    blurb:
      "Ridgefield sits in one of the highest deer-pressure zones in Fairfield County. Our 8-foot near-invisible deer fence excludes them completely from vegetable gardens and ornamental beds without turning your property into a compound.",
    lotProfile:
      "Two- to five-acre wooded lots with ledge and stone walls, particularly along West Mountain and Silver Spring.",
    deerPressure: "very-high",
    notableAttributes: ["Very high deer density", "Historic district considerations near Main Street"],
  },
  {
    slug: "wilton",
    name: "Wilton",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.1954,
    lng: -73.4373,
    blurb:
      "Wilton's two-acre-minimum zoning means most projects here are meaningful perimeters. We routinely install 1,500+ linear-foot systems along the Norwalk River corridor and up onto the wooded ridgelines.",
    lotProfile: "Two-acre minimum wooded lots, often with wetlands review required.",
    deerPressure: "very-high",
    notableAttributes: ["Wetlands commission review common", "2-acre zoning"],
  },
  {
    slug: "weston",
    name: "Weston",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.2001,
    lng: -73.3801,
    blurb:
      "Weston's rural character and 2-acre zoning make it one of our favorite Fairfield County towns to work in — lots of trees, thoughtful homeowners, and no HOAs to navigate. Deer pressure is intense here.",
    lotProfile: "Two- to five-acre heavily wooded parcels with almost no public street frontage.",
    deerPressure: "very-high",
    notableAttributes: ["No town center commercial zone", "Very wooded", "No HOAs"],
  },
  {
    slug: "fairfield",
    name: "Fairfield",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.1408,
    lng: -73.2613,
    blurb:
      "From Southport's harbor cottages to Greenfield Hill's estate lots, Fairfield is one of the most varied towns we work in. We match materials and mesh height to the neighborhood — coastal in Southport, taller inland.",
    lotProfile: "Quarter-acre coastal lots up to multi-acre inland estates.",
    deerPressure: "moderate",
    notableAttributes: ["Historic Southport", "Coastal salt exposure"],
  },

  // ================= CONNECTICUT — Litchfield County =================
  {
    slug: "litchfield",
    name: "Litchfield",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.7473,
    lng: -73.1887,
    blurb:
      "Litchfield's colonial-era estates and horse farms demand fencing that respects the historic setting. Our black-mesh systems disappear against stone walls and mature trees without triggering historic-district review.",
    lotProfile: "Five- to twenty-acre farms and estates with open pasture and mature woodlots.",
    deerPressure: "very-high",
    notableAttributes: ["Historic district", "Horse farms", "Open pasture perimeters"],
  },
  {
    slug: "washington",
    name: "Washington",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.6337,
    lng: -73.3115,
    blurb:
      "Washington Depot and the surrounding hills are prime territory for our largest deer and garden installs — long perimeters, meaningful elevation change, and homeowners who want the fence to vanish.",
    lotProfile: "Five- to fifty-acre parcels with hills, ledge, and hardwood forest.",
    deerPressure: "very-high",
    notableAttributes: ["Rolling hills", "Very large perimeters"],
  },
  {
    slug: "kent",
    name: "Kent",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.7237,
    lng: -73.4776,
    blurb:
      "Kent's Housatonic River valley properties see heavy deer and turkey pressure. Our systems perform on the steep grades and rocky soil that make traditional fencing brutal to install here.",
    lotProfile: "Riverfront and hillside parcels with significant grade change.",
    deerPressure: "very-high",
    notableAttributes: ["Housatonic River corridor", "Steep grades"],
  },
  {
    slug: "roxbury",
    name: "Roxbury",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.5537,
    lng: -73.3037,
    blurb:
      "Roxbury remains one of the quietest, most rural towns in Litchfield County. Estates here run large and deer are unrelenting — we build long perimeters that stay invisible from the road.",
    lotProfile: "Ten- to fifty-acre estates and gentleman's farms.",
    deerPressure: "very-high",
    notableAttributes: ["Very rural", "Large estates"],
  },

  // ================= CONNECTICUT — Hartford County =================
  {
    slug: "west-hartford",
    name: "West Hartford",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "hartford-county-ct",
    lat: 41.7621,
    lng: -72.7420,
    blurb:
      "West Hartford's tree-lined neighborhoods are ideal for our low-visual-impact fencing. We install cleanly in tight suburban settings and integrate around the mature landscaping that defines the town.",
    lotProfile: "Quarter-acre to two-acre suburban lots with mature landscaping.",
    deerPressure: "moderate",
    notableAttributes: ["Dense mature tree canopy", "Walkable neighborhoods"],
  },
  {
    slug: "farmington",
    name: "Farmington",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "hartford-county-ct",
    lat: 41.7201,
    lng: -72.8320,
    blurb:
      "Farmington's mix of historic center properties and newer development on Talcott Mountain gives us a wide range of projects. Deer pressure climbs sharply above the valley floor.",
    lotProfile: "Half-acre village lots to five-acre hillside parcels.",
    deerPressure: "high",
    notableAttributes: ["Talcott Mountain deer pressure", "Historic Farmington village"],
  },

  // ================= NEW YORK — Westchester =================
  {
    slug: "bedford",
    name: "Bedford",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 41.2043,
    lng: -73.6407,
    blurb:
      "Bedford, Bedford Hills, and Bedford Village are our most consistent northern Westchester market. Four-acre zoning, deep tree cover, and estate-scale perimeters make this the perfect environment for our system.",
    lotProfile: "Four-acre minimum zoning; multi-acre wooded and pasture parcels.",
    deerPressure: "very-high",
    notableAttributes: ["4-acre zoning", "Horse country", "Very high deer"],
  },
  {
    slug: "chappaqua",
    name: "Chappaqua",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 41.1573,
    lng: -73.7654,
    blurb:
      "Chappaqua's rolling wooded neighborhoods are prime deer and coyote habitat. Our fences give families back use of their yards without adding an eyesore.",
    lotProfile: "One- to three-acre wooded suburban lots.",
    deerPressure: "very-high",
    notableAttributes: ["Rolling wooded terrain", "Family suburbs"],
  },
  {
    slug: "scarsdale",
    name: "Scarsdale",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 40.9884,
    lng: -73.7857,
    blurb:
      "Scarsdale properties are meticulously landscaped and closely observed by neighbors — a fence has to disappear or it doesn't get installed. Our black poly systems do.",
    lotProfile: "Quarter-acre to one-acre manicured suburban lots.",
    deerPressure: "high",
    notableAttributes: ["Design-conscious neighborhoods", "Tight lot lines"],
  },
  {
    slug: "rye",
    name: "Rye",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 40.9815,
    lng: -73.6837,
    blurb:
      "From Milton Point to Greenhaven, Rye combines shoreline exposure with tight lot lines. Our coastal-grade hardware and unobtrusive mesh are built for exactly this environment.",
    lotProfile: "Coastal quarter- to two-acre lots.",
    deerPressure: "moderate",
    notableAttributes: ["Long Island Sound salt", "Country clubs and shoreline neighborhoods"],
  },
  {
    slug: "pound-ridge",
    name: "Pound Ridge",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 41.2076,
    lng: -73.5768,
    blurb:
      "Pound Ridge is essentially all forest and ledge — the toughest terrain in Westchester and the highest deer pressure. We routinely install multi-thousand linear-foot perimeters here that never touch a lawn.",
    lotProfile: "Four-acre-minimum heavily wooded lots.",
    deerPressure: "very-high",
    notableAttributes: ["4-acre zoning", "Rocky ledge", "Almost entirely wooded"],
  },
  {
    slug: "katonah",
    name: "Katonah",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 41.2565,
    lng: -73.6832,
    blurb:
      "Katonah's mix of historic village homes and estate properties along the Muscoot Reservoir keeps us busy. The reservoir corridor sees intense deer traffic year-round.",
    lotProfile: "Half-acre village lots to five-acre estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Muscoot Reservoir corridor", "Historic village center"],
  },
  {
    slug: "bronxville",
    name: "Bronxville",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 40.9401,
    lng: -73.8337,
    blurb:
      "Bronxville's tight village-scale lots reward our least-visible installations. We work carefully within the village's design review culture and finish projects fast.",
    lotProfile: "Small village lots, dense hedging common.",
    deerPressure: "low",
    notableAttributes: ["Village design review", "Compact lots"],
  },

  // ================= NEW YORK — Hudson Valley =================
  {
    slug: "rhinebeck",
    name: "Rhinebeck",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.9270,
    lng: -73.9124,
    blurb:
      "Rhinebeck's Dutchess County estate belt is a natural fit for our largest deer-exclusion projects. Long perimeters, elevation change, and homeowners who want fencing that respects the historic setting.",
    lotProfile: "Ten- to fifty-acre estates and gentleman's farms.",
    deerPressure: "very-high",
    notableAttributes: ["Historic estates", "Very large perimeters"],
  },
  {
    slug: "millbrook",
    name: "Millbrook",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.7862,
    lng: -73.6935,
    blurb:
      "Millbrook is horse country — big open perimeters, working farms, and serious deer, coyote, and bear pressure. Our systems hold up on real acreage.",
    lotProfile: "Ten-acre-plus farms and estates with open pasture and woodlots.",
    deerPressure: "very-high",
    notableAttributes: ["Horse farms", "Very large open perimeters"],
  },
  {
    slug: "woodstock",
    name: "Woodstock",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 42.0409,
    lng: -74.1179,
    blurb:
      "Woodstock and the surrounding Catskills foothills are deer, bear, and turkey country. Our fences protect artist studios and vegetable gardens from becoming a wildlife buffet.",
    lotProfile: "Two- to twenty-acre wooded hillside parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Bear country", "Steep wooded lots"],
  },
  {
    slug: "cold-spring",
    name: "Cold Spring",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.4198,
    lng: -73.9557,
    blurb:
      "Cold Spring's Hudson Highlands terrain is dramatic — ledge, elevation, and river views to preserve. Our systems install cleanly on grades that stop traditional post-and-rail cold.",
    lotProfile: "One- to ten-acre hillside parcels with significant grade.",
    deerPressure: "very-high",
    notableAttributes: ["Hudson Highlands terrain", "Preserved view corridors"],
  },

  // ================= NEW YORK — Saratoga =================
  {
    slug: "saratoga-springs",
    name: "Saratoga Springs",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "saratoga-county-ny",
    lat: 43.0831,
    lng: -73.7846,
    blurb:
      "Saratoga's mix of racing-season estates, horse farms, and in-town Victorians keeps our schedule full. We install through the shoulder seasons and even in winter.",
    lotProfile: "Village lots to multi-acre farms outside town.",
    deerPressure: "high",
    notableAttributes: ["Horse-racing culture", "Historic in-town properties"],
  },

  // ================= NEW YORK — The Hamptons =================
  {
    slug: "east-hampton",
    name: "East Hampton",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "the-hamptons-ny",
    lat: 40.9634,
    lng: -72.1848,
    blurb:
      "East Hampton properties combine ocean and bayfront exposure with heavy East End deer pressure and tick-driven demand for real perimeter control. Our systems are engineered for salt, sand, and wind.",
    lotProfile: "Half-acre village lots to multi-acre oceanfront estates.",
    deerPressure: "very-high",
    notableAttributes: ["Salt and wind exposure", "Tick-driven demand", "ARB review"],
  },
  {
    slug: "southampton",
    name: "Southampton",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "the-hamptons-ny",
    lat: 40.8843,
    lng: -72.3898,
    blurb:
      "Southampton's estate-belt hedgerows are a natural home for our system — the fencing disappears into the privet and does its job without changing the character of the property.",
    lotProfile: "One- to five-acre estate lots with mature privet.",
    deerPressure: "very-high",
    notableAttributes: ["Privet hedgerows", "Estate belt", "ARB review"],
  },

  // ================= MASSACHUSETTS — Berkshires =================
  {
    slug: "lenox",
    name: "Lenox",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "the-berkshires-ma",
    lat: 42.3562,
    lng: -73.2843,
    blurb:
      "Lenox and its Gilded Age estate belt are ideal for our largest deer projects. We install through Berkshires winters when the ground allows and integrate around historic landscape features.",
    lotProfile: "Multi-acre historic estates with mature landscape design.",
    deerPressure: "very-high",
    notableAttributes: ["Historic estates", "Tanglewood corridor"],
  },
  {
    slug: "stockbridge",
    name: "Stockbridge",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "the-berkshires-ma",
    lat: 42.2870,
    lng: -73.3123,
    blurb:
      "Stockbridge properties see intense deer pressure and the town's conservation-commission review takes fencing plans seriously. Our low-impact systems clear both without a fight.",
    lotProfile: "Two- to twenty-acre wooded lots.",
    deerPressure: "very-high",
    notableAttributes: ["Conservation commission review", "Historic village center"],
  },
  {
    slug: "great-barrington",
    name: "Great Barrington",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "the-berkshires-ma",
    lat: 42.1954,
    lng: -73.3620,
    blurb:
      "Great Barrington's mix of in-town Victorians and outlying farms gives us a wide range of project types. Deer, bear, and turkey pressure is consistent year-round.",
    lotProfile: "Half-acre village lots up to twenty-acre farms.",
    deerPressure: "very-high",
    notableAttributes: ["Bear country", "Working farms"],
  },
  {
    slug: "lee",
    name: "Lee",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "the-berkshires-ma",
    lat: 42.3037,
    lng: -73.2487,
    blurb:
      "Lee's wooded lots on the Housatonic corridor are prime deer territory. We install for both year-round residents and second-home owners preparing for summer garden season.",
    lotProfile: "One- to ten-acre wooded lots.",
    deerPressure: "very-high",
    notableAttributes: ["Housatonic corridor", "Second-home owners"],
  },

  // ================= MASSACHUSETTS — MetroWest =================
  {
    slug: "wellesley",
    name: "Wellesley",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.2968,
    lng: -71.2924,
    blurb:
      "Wellesley's mix of family lots and estate parcels demands fencing that disappears against the mature landscaping. Deer pressure is real, especially west of Route 16.",
    lotProfile: "Half-acre to two-acre suburban lots.",
    deerPressure: "high",
    notableAttributes: ["Mature suburban landscaping", "Family-oriented"],
  },
  {
    slug: "weston-ma",
    name: "Weston",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.3665,
    lng: -71.3033,
    blurb:
      "Weston's two-acre-minimum zoning and heavy tree cover make it one of our best MetroWest markets. Long wooded perimeters with meaningful deer pressure.",
    lotProfile: "Two- to five-acre wooded lots.",
    deerPressure: "very-high",
    notableAttributes: ["2-acre zoning", "Heavy tree cover"],
  },
  {
    slug: "concord",
    name: "Concord",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.4604,
    lng: -71.3489,
    blurb:
      "Concord's conservation-heavy character and historic setting require sensitive fence design. Our low-impact systems are routinely approved where visible fencing would be denied.",
    lotProfile: "One- to five-acre lots with conservation abutters.",
    deerPressure: "high",
    notableAttributes: ["Conservation commission review", "Historic district"],
  },
  {
    slug: "lincoln",
    name: "Lincoln",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.4256,
    lng: -71.3034,
    blurb:
      "Lincoln's conservation ethic and 2-acre zoning make it a great fit for our almost-invisible systems. Long perimeters through mixed woodland with genuine wildlife pressure.",
    lotProfile: "Two-acre-minimum wooded lots with conservation abutters.",
    deerPressure: "very-high",
    notableAttributes: ["2-acre zoning", "Conservation-first culture"],
  },

  // ================= MASSACHUSETTS — Cape Cod =================
  {
    slug: "chatham",
    name: "Chatham",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-cod-ma",
    lat: 41.6820,
    lng: -69.9598,
    blurb:
      "Chatham's shoreline properties see salt spray, wind, and the Cape's steadily growing deer population. We install with coastal-grade hardware and screen the fence into the existing hedging.",
    lotProfile: "Quarter-acre coastal lots to two-acre inland parcels.",
    deerPressure: "high",
    notableAttributes: ["Coastal salt exposure", "Historic village"],
  },
  {
    slug: "osterville",
    name: "Osterville",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-cod-ma",
    lat: 41.6265,
    lng: -70.3814,
    blurb:
      "Osterville's estate belt and the Oyster Harbors community are natural markets for our system. Coastal exposure, careful design review, and homeowners who want the fence out of sight.",
    lotProfile: "Half-acre to five-acre coastal estate lots.",
    deerPressure: "high",
    notableAttributes: ["Oyster Harbors and estate belt", "Design review"],
  },

  // ================= MASSACHUSETTS — North Shore =================
  {
    slug: "manchester-by-the-sea",
    name: "Manchester-by-the-Sea",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.5776,
    lng: -70.7700,
    blurb:
      "Manchester's rocky coastline and tightly-designed properties call for restrained fencing that plays with the mature landscape. Our black systems fade into hemlock and rhododendron.",
    lotProfile: "Half-acre to two-acre coastal parcels.",
    deerPressure: "moderate",
    notableAttributes: ["Rocky coastline", "Mature evergreen landscape"],
  },

  // ================= MASSACHUSETTS — South Shore =================
  {
    slug: "cohasset",
    name: "Cohasset",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "south-shore-ma",
    lat: 42.2418,
    lng: -70.8034,
    blurb:
      "Cohasset's shoreline neighborhoods and Little Harbor community want fencing that respects the coastal setting. Our unobtrusive systems clear ARB review and hold up against Nor'easters.",
    lotProfile: "Half-acre to two-acre coastal lots.",
    deerPressure: "moderate",
    notableAttributes: ["ARB review", "Coastal salt exposure"],
  },

  // ================= CONNECTICUT — Phase 2 =================
  {
    slug: "stamford",
    name: "Stamford",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.0534,
    lng: -73.5387,
    blurb:
      "Stamford's North Stamford and Westover neighborhoods sit on wooded acreage that draws heavy deer traffic despite the city address. Our non-electric systems fit the tree-cover terrain without cutting sightlines.",
    lotProfile: "One- to four-acre wooded lots north of the Merritt, quarter-acre suburban lots closer to the parkway.",
    deerPressure: "high",
    notableAttributes: ["North Stamford watershed deer corridors", "Mixed suburban/estate density"],
  },
  {
    slug: "norwalk",
    name: "Norwalk",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.1177,
    lng: -73.4082,
    blurb:
      "Silvermine, Cranbury, and West Norwalk homeowners want fencing that disappears into hedgerows while actually containing dogs on lots the city's dense zoning barely permits.",
    lotProfile: "Half-acre to two-acre lots with mature landscape, common HOA setbacks along Silvermine River.",
    deerPressure: "high",
    notableAttributes: ["Silvermine River watershed", "Suburban tree canopy"],
  },
  {
    slug: "easton",
    name: "Easton",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.2529,
    lng: -73.2984,
    blurb:
      "Easton's three-acre zoning and reservoir-adjacent lots see some of Fairfield County's worst deer and tick pressure. Our garden and perimeter systems are engineered for the density.",
    lotProfile: "Three-acre minimum zoning, heavily wooded with reservoir buffers.",
    deerPressure: "very-high",
    notableAttributes: ["Three-acre zoning", "Aspetuck Reservoir edge", "Severe tick/Lyme pressure"],
  },
  {
    slug: "redding",
    name: "Redding",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.3026,
    lng: -73.3843,
    blurb:
      "Redding's rural character, land-trust preserves, and two-acre minimums produce the exact conditions our deer fencing was designed for. Every install respects the town's conservation ethic.",
    lotProfile: "Two- to five-acre wooded parcels, many abutting land trust preserves.",
    deerPressure: "very-high",
    notableAttributes: ["Redding Land Trust preserves", "Old-growth stone walls"],
  },
  {
    slug: "woodbury",
    name: "Woodbury",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.5445,
    lng: -73.2085,
    blurb:
      "Woodbury's antique homes and historic-district oversight demand fencing that reads period-appropriate. Our black poly and matte metal disappear against the town's mature landscape.",
    lotProfile: "Two- to six-acre rural residential, many in historic district review.",
    deerPressure: "high",
    notableAttributes: ["Historic district review", "Antique-home neighborhoods"],
  },
  {
    slug: "new-milford",
    name: "New Milford",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.5764,
    lng: -73.4079,
    blurb:
      "New Milford's Candlewood Lake and Housatonic River corridors funnel deer through residential lots year-round. Our systems handle the terrain — from lake frontage to wooded ridge.",
    lotProfile: "One- to five-acre parcels, lake frontage common in Candlewood neighborhoods.",
    deerPressure: "very-high",
    notableAttributes: ["Candlewood Lake frontage", "Housatonic River corridor"],
  },
  {
    slug: "sharon",
    name: "Sharon",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.8790,
    lng: -73.4762,
    blurb:
      "Sharon's rolling estates and horse farms want deer exclusion at scale without the visual weight of woven-wire. Our tall poly systems run thousands of linear feet unobtrusively.",
    lotProfile: "Estate parcels 5–25+ acres, horse farms, and open meadow.",
    deerPressure: "very-high",
    notableAttributes: ["Weekend estate country", "Open meadow deer pressure"],
  },
  {
    slug: "salisbury",
    name: "Salisbury",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.9848,
    lng: -73.4223,
    blurb:
      "Salisbury and Lakeville homeowners protect ornamental gardens and orchards from constant deer browse. Our garden-perimeter systems clear conservation-commission review.",
    lotProfile: "Lakefront and hillside estates, 3–20 acres.",
    deerPressure: "very-high",
    notableAttributes: ["Lakeville / Twin Lakes", "Weekend residents from NYC"],
  },
  {
    slug: "simsbury",
    name: "Simsbury",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "hartford-county-ct",
    lat: 41.8756,
    lng: -72.8009,
    blurb:
      "Simsbury's Farmington River valley combines strong schools, deep suburban lots, and Talcott Mountain deer that route through every backyard. Our fencing keeps dogs safe on the town's greenway network.",
    lotProfile: "Half-acre to two-acre residential lots with wooded rear boundaries.",
    deerPressure: "high",
    notableAttributes: ["Farmington River valley", "Talcott Mountain deer corridors"],
  },
  {
    slug: "avon",
    name: "Avon",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "hartford-county-ct",
    lat: 41.8098,
    lng: -72.8306,
    blurb:
      "Avon Mountain and the surrounding neighborhoods produce steady deer pressure into ornamental beds. Our systems handle the ridge terrain and preserve views.",
    lotProfile: "Half-acre to three-acre lots on and around Avon Mountain.",
    deerPressure: "high",
    notableAttributes: ["Avon Mountain ridge", "Suburban ornamental gardens"],
  },
  {
    slug: "glastonbury",
    name: "Glastonbury",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "hartford-county-ct",
    lat: 41.7124,
    lng: -72.6081,
    blurb:
      "Glastonbury's orchards, riverfront neighborhoods, and Minnechaug Mountain slopes all see year-round deer traffic. We install perimeter and orchard-style exclusion at any scale.",
    lotProfile: "Half-acre to five-acre lots, including working orchards and riverfront estates.",
    deerPressure: "high",
    notableAttributes: ["Working orchards", "Connecticut River frontage"],
  },
  {
    slug: "madison",
    name: "Madison",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "new-haven-county-suburbs-ct",
    lat: 41.2795,
    lng: -72.5987,
    blurb:
      "Madison's shoreline neighborhoods and inland woods run everything from coastal cottages to horse-property acreage. Our systems handle both without visual intrusion.",
    lotProfile: "Coastal quarter-acre lots to inland five-acre parcels.",
    deerPressure: "high",
    notableAttributes: ["Long Island Sound shoreline", "Hammonasset conservation land"],
  },
  {
    slug: "guilford",
    name: "Guilford",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "new-haven-county-suburbs-ct",
    lat: 41.2887,
    lng: -72.6817,
    blurb:
      "Guilford's historic green, coastal neighborhoods, and back-country farms all deal with high deer pressure. Our fencing respects the town's colonial character.",
    lotProfile: "Half-acre coastal lots and multi-acre inland farms.",
    deerPressure: "high",
    notableAttributes: ["Historic Guilford Green", "Working farmland inland"],
  },

  // ================= NEW YORK — Phase 2 =================
  {
    slug: "armonk",
    name: "Armonk",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 41.1264,
    lng: -73.7140,
    blurb:
      "Armonk's Whippoorwill and Windmill Farm neighborhoods sit on the kind of wooded acreage where deer treat gardens as a food court. Our systems protect ornamental beds without changing the character of the property.",
    lotProfile: "Two- to five-acre wooded lots, many in association neighborhoods.",
    deerPressure: "very-high",
    notableAttributes: ["Windmill Farm HOA", "Whippoorwill Club neighborhood"],
  },
  {
    slug: "larchmont",
    name: "Larchmont",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 40.9331,
    lng: -73.7601,
    blurb:
      "Larchmont Manor and the village grid pack elegant homes on tight lots where a fence has to be nearly invisible from the street. Our matte metal and poly systems fit that brief.",
    lotProfile: "Quarter- to half-acre village lots with mature hedges.",
    deerPressure: "moderate",
    notableAttributes: ["Larchmont Manor Park", "Long Island Sound access"],
  },
  {
    slug: "mamaroneck",
    name: "Mamaroneck",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 40.9490,
    lng: -73.7351,
    blurb:
      "Mamaroneck's Orienta and Shore Acres neighborhoods want fencing that respects the coastal architecture. Our systems hold up against salt exposure and Nor'easter winds.",
    lotProfile: "Coastal half-acre to acre lots, many with waterfront.",
    deerPressure: "moderate",
    notableAttributes: ["Orienta Point waterfront", "Sound-facing exposure"],
  },
  {
    slug: "irvington",
    name: "Irvington",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 41.0420,
    lng: -73.8687,
    blurb:
      "Irvington's steep Hudson-facing hillsides and historic homes need fencing that respects both terrain and architectural review. Our low-visual-impact systems clear ARB and hold the grade.",
    lotProfile: "Half-acre to three-acre hillside lots with Hudson views.",
    deerPressure: "high",
    notableAttributes: ["Hudson River hillside", "Architectural review culture"],
  },
  {
    slug: "hyde-park",
    name: "Hyde Park",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.7854,
    lng: -73.9332,
    blurb:
      "Hyde Park's Hudson estates and farm parcels see the full brunt of Dutchess County deer pressure. Our tall exclusion systems protect orchards, cutting gardens, and pastures.",
    lotProfile: "Estate parcels 5–30 acres along the Hudson corridor.",
    deerPressure: "very-high",
    notableAttributes: ["Hudson River estates", "Working farmland"],
  },
  {
    slug: "kingston",
    name: "Kingston",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.9270,
    lng: -73.9974,
    blurb:
      "Kingston's Stockade district and outlying hillside neighborhoods pair historic-review oversight with heavy Catskill-foothill deer traffic. Our systems fit both.",
    lotProfile: "Village lots plus outlying two- to ten-acre wooded parcels.",
    deerPressure: "high",
    notableAttributes: ["Stockade Historic District", "Catskill foothills"],
  },
  {
    slug: "new-paltz",
    name: "New Paltz",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.7476,
    lng: -74.0871,
    blurb:
      "New Paltz sits at the base of the Shawangunk Ridge, where deer pressure and rocky ledge combine into a hard install. We engineer post depth and layout for the terrain.",
    lotProfile: "Two- to twenty-acre parcels, many with ledge outcrops.",
    deerPressure: "very-high",
    notableAttributes: ["Shawangunk Ridge", "Rocky ledge post-setting"],
  },
  {
    slug: "sag-harbor",
    name: "Sag Harbor",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "the-hamptons-ny",
    lat: 40.9948,
    lng: -72.2946,
    blurb:
      "Sag Harbor Village lots and North Haven estates share tight ARB review and heavy deer pressure. Our black systems disappear into the hedgerow culture of the East End.",
    lotProfile: "Village quarter-acre to North Haven three-acre estates.",
    deerPressure: "very-high",
    notableAttributes: ["North Haven peninsula", "Tick-heavy East End deer"],
  },
  {
    slug: "bridgehampton",
    name: "Bridgehampton",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "the-hamptons-ny",
    lat: 40.9401,
    lng: -72.3020,
    blurb:
      "Bridgehampton's farm parcels and horse properties sit in the middle of the East End's worst deer corridor. Our tall exclusion protects pastures, orchards, and gardens.",
    lotProfile: "Two- to twenty-acre farm and estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Farm belt", "Horse-property setbacks"],
  },
  {
    slug: "water-mill",
    name: "Water Mill",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "the-hamptons-ny",
    lat: 40.9109,
    lng: -72.3459,
    blurb:
      "Water Mill's estate parcels along Mecox Bay and the ocean road want fencing that reads as part of the landscape. Our systems clear ARB and integrate with mature privet.",
    lotProfile: "Estate parcels 2–15 acres, ocean- and bay-facing.",
    deerPressure: "very-high",
    notableAttributes: ["Mecox Bay frontage", "Privet hedge integration"],
  },
  {
    slug: "cutchogue",
    name: "Cutchogue",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 41.0117,
    lng: -72.4881,
    blurb:
      "Cutchogue's vineyards, farms, and waterfront homes want deer exclusion that scales from a rose garden to a full agricultural parcel. We handle both.",
    lotProfile: "Vineyard and farm parcels, plus half-acre waterfront lots.",
    deerPressure: "very-high",
    notableAttributes: ["North Fork wine country", "Agricultural exclusion at scale"],
  },
  {
    slug: "southold",
    name: "Southold",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 41.0645,
    lng: -72.4256,
    blurb:
      "Southold's mix of waterfront cottages, working farms, and antique homes needs fencing that respects both agricultural use and historic review.",
    lotProfile: "Half-acre waterfront to five-acre farm parcels.",
    deerPressure: "very-high",
    notableAttributes: ["North Fork farmland", "Bayfront and Sound-front lots"],
  },
  {
    slug: "ballston-spa",
    name: "Ballston Spa",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "saratoga-county-ny",
    lat: 43.0031,
    lng: -73.8493,
    blurb:
      "Ballston Spa's village edge and surrounding horse country need fencing that works for both suburban dog-containment and farm-scale deer exclusion.",
    lotProfile: "Village lots plus multi-acre horse and hobby-farm parcels.",
    deerPressure: "high",
    notableAttributes: ["Saratoga horse country edge", "Village + rural mix"],
  },

  // ================= MASSACHUSETTS — Phase 2 =================
  {
    slug: "dover",
    name: "Dover",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.2459,
    lng: -71.2836,
    blurb:
      "Dover's one-acre zoning, wooded lots, and Charles River frontage create the exact conditions our deer exclusion is engineered for. Every install respects the town's conservation commission review.",
    lotProfile: "One- to five-acre wooded lots, many with river or brook frontage.",
    deerPressure: "very-high",
    notableAttributes: ["Charles River frontage", "Conservation commission review"],
  },
  {
    slug: "sherborn",
    name: "Sherborn",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.2384,
    lng: -71.3695,
    blurb:
      "Sherborn's two-acre minimum zoning and horse-friendly ethos make it a natural fit for our tall exclusion systems. We handle everything from garden perimeters to pasture lines.",
    lotProfile: "Two- to ten-acre parcels, many with barns and pasture.",
    deerPressure: "very-high",
    notableAttributes: ["Two-acre minimum zoning", "Horse-property culture"],
  },
  {
    slug: "sudbury",
    name: "Sudbury",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.3834,
    lng: -71.4162,
    blurb:
      "Sudbury's colonial-era homes, orchards, and conservation land all see steady deer pressure. Our systems clear conservation review and preserve historic sightlines.",
    lotProfile: "One- to three-acre lots plus working orchards and farms.",
    deerPressure: "high",
    notableAttributes: ["Historic colonial architecture", "Working orchards"],
  },
  {
    slug: "carlisle",
    name: "Carlisle",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.5292,
    lng: -71.3495,
    blurb:
      "Carlisle's two-acre minimum, extensive conservation land, and cranberry-bog history produce the highest deer pressure in MetroWest. Our fencing is built for it.",
    lotProfile: "Two- to eight-acre wooded parcels abutting conservation land.",
    deerPressure: "very-high",
    notableAttributes: ["Two-acre minimum zoning", "Extensive town conservation land"],
  },
  {
    slug: "williamstown",
    name: "Williamstown",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "the-berkshires-ma",
    lat: 42.7112,
    lng: -73.2037,
    blurb:
      "Williamstown's college-town homes and outlying estates face relentless northern Berkshire deer pressure. Our exclusion protects ornamental gardens and orchards year-round.",
    lotProfile: "Half-acre village lots to multi-acre hillside estates.",
    deerPressure: "very-high",
    notableAttributes: ["Northern Berkshires", "Williams College campus edge"],
  },
  {
    slug: "west-stockbridge",
    name: "West Stockbridge",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "the-berkshires-ma",
    lat: 42.3348,
    lng: -73.3737,
    blurb:
      "West Stockbridge's weekend estates and working farms sit in the middle of the southern Berkshire deer corridor. Our tall systems disappear against the hillside.",
    lotProfile: "Two- to twenty-acre rural residential parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Weekend estate country", "Rural hillside terrain"],
  },
  {
    slug: "wellfleet",
    name: "Wellfleet",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-cod-ma",
    lat: 41.9362,
    lng: -70.0331,
    blurb:
      "Wellfleet's National Seashore-adjacent homes and kettle-pond neighborhoods need fencing that respects the conservation setting and holds up against salt air.",
    lotProfile: "Half-acre to two-acre wooded coastal lots.",
    deerPressure: "high",
    notableAttributes: ["Cape Cod National Seashore edge", "Kettle-pond neighborhoods"],
  },
  {
    slug: "orleans",
    name: "Orleans",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-cod-ma",
    lat: 41.7900,
    lng: -69.9894,
    blurb:
      "Orleans homeowners on Pleasant Bay and Nauset want fencing that survives coastal exposure while blending into the pitch-pine landscape.",
    lotProfile: "Half-acre coastal lots, many with water access.",
    deerPressure: "moderate",
    notableAttributes: ["Pleasant Bay frontage", "Nauset Beach access"],
  },
  {
    slug: "barnstable",
    name: "Barnstable",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-cod-ma",
    lat: 41.7003,
    lng: -70.3002,
    blurb:
      "Barnstable's villages — from Cotuit to Cummaquid — combine antique homes, coastal exposure, and conservation-commission review. Our systems fit all three.",
    lotProfile: "Village lots plus multi-acre coastal parcels.",
    deerPressure: "moderate",
    notableAttributes: ["Historic villages", "Conservation-commission review"],
  },
  {
    slug: "marblehead",
    name: "Marblehead",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.5001,
    lng: -70.8578,
    blurb:
      "Marblehead's Old Town lanes and Neck estates want fencing that respects colonial-era streetscape and survives ocean exposure. Our systems clear historic review.",
    lotProfile: "Village quarter-acre lots plus Neck estate parcels.",
    deerPressure: "moderate",
    notableAttributes: ["Old Town historic district", "Marblehead Neck estates"],
  },
  {
    slug: "beverly",
    name: "Beverly",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.5584,
    lng: -70.8800,
    blurb:
      "Beverly Farms and Prides Crossing homeowners want fencing that fits estate-scale properties without shouting. Our black poly and matte metal disappear into the mature landscape.",
    lotProfile: "One- to five-acre estate parcels along the coast.",
    deerPressure: "high",
    notableAttributes: ["Beverly Farms estates", "Prides Crossing neighborhood"],
  },
  {
    slug: "hingham",
    name: "Hingham",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "south-shore-ma",
    lat: 42.2418,
    lng: -70.8898,
    blurb:
      "Hingham's Crow Point, World's End edge, and Main Street antique homes each carry different review standards. Our fencing fits each context.",
    lotProfile: "Village lots to two-acre coastal parcels.",
    deerPressure: "moderate",
    notableAttributes: ["World's End reservation edge", "Historic Main Street"],
  },
  {
    slug: "duxbury",
    name: "Duxbury",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "south-shore-ma",
    lat: 42.0418,
    lng: -70.6720,
    blurb:
      "Duxbury's cranberry bogs, coastal estates, and colonial homes make for a wide range of fence contexts. Our systems handle every scale.",
    lotProfile: "Half-acre coastal to five-acre inland parcels.",
    deerPressure: "high",
    notableAttributes: ["Duxbury Beach", "Working cranberry bogs"],
  },
  {
    slug: "scituate",
    name: "Scituate",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "south-shore-ma",
    lat: 42.1953,
    lng: -70.7256,
    blurb:
      "Scituate's cliffside neighborhoods and inland woods each have distinct fencing needs — from salt-resistant coastal runs to deer exclusion in the back country.",
    lotProfile: "Coastal quarter-acre lots to inland two-acre parcels.",
    deerPressure: "high",
    notableAttributes: ["Coastal cliffs", "Inland wooded parcels"],
  },
  // ================= CONNECTICUT — Fairfield County (cont.) =================
  {
    slug: "old-greenwich",
    name: "Old Greenwich",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.0334,
    lng: -73.5648,
    blurb:
      "Old Greenwich's shoreline lots and village neighborhoods call for fencing that stays quiet against clapboard and hedgerow while surviving salt spray off the Sound.",
    lotProfile: "Quarter- to one-acre coastal and village lots.",
    deerPressure: "moderate",
    notableAttributes: ["Coastal salt exposure", "Village association review"],
  },
  {
    slug: "riverside",
    name: "Riverside",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.0334,
    lng: -73.5851,
    blurb:
      "Riverside's tight village grid and waterfront pockets need fencing that reads as landscape, not hardware. Our poly and metal systems disappear into hedgerows.",
    lotProfile: "Quarter- to half-acre village lots, some waterfront.",
    deerPressure: "moderate",
    notableAttributes: ["Waterfront edges", "Village density"],
  },
  {
    slug: "cos-cob",
    name: "Cos Cob",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.0334,
    lng: -73.5987,
    blurb:
      "Cos Cob's mix of historic homes and newer builds along the Mianus corridor sees consistent deer pressure. We tune fencing to lot, canopy, and dog.",
    lotProfile: "Quarter-acre village to two-acre wooded parcels.",
    deerPressure: "high",
    notableAttributes: ["Mianus River corridor", "Mixed historic and new construction"],
  },
  {
    slug: "southport",
    name: "Southport",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "fairfield-county-ct",
    lat: 41.1379,
    lng: -73.2887,
    blurb:
      "Southport's historic village and Sasco Hill estates each require careful visual restraint. Our near-invisible systems clear historic-district scrutiny.",
    lotProfile: "Village half-acre to multi-acre estate parcels.",
    deerPressure: "high",
    notableAttributes: ["Historic district", "Shoreline salt exposure"],
  },
  // ================= CONNECTICUT — Hartford County (cont.) =================
  {
    slug: "canton",
    name: "Canton",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "hartford-county-ct",
    lat: 41.8329,
    lng: -72.8934,
    blurb:
      "Canton's Farmington River valley lots see steady deer and coyote pressure. Our 8-foot deer fence and dog systems perform where woods meet field.",
    lotProfile: "One- to five-acre wooded and pasture parcels.",
    deerPressure: "high",
    notableAttributes: ["Farmington River valley", "Rural residential"],
  },
  {
    slug: "bloomfield",
    name: "Bloomfield",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "hartford-county-ct",
    lat: 41.8265,
    lng: -72.7301,
    blurb:
      "Bloomfield homeowners along Wintonbury and Tunxis Trail want fencing that respects mature landscaping while containing active dogs.",
    lotProfile: "Half- to two-acre suburban and wooded parcels.",
    deerPressure: "moderate",
    notableAttributes: ["Mature suburban landscaping", "Wooded edges"],
  },
  {
    slug: "wethersfield",
    name: "Wethersfield",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "hartford-county-ct",
    lat: 41.7142,
    lng: -72.6528,
    blurb:
      "Old Wethersfield's colonial homes need fencing that reads as period-appropriate. Black metal and near-invisible poly integrate cleanly.",
    lotProfile: "Historic village lots to half-acre suburban parcels.",
    deerPressure: "low",
    notableAttributes: ["Historic district review", "Colonial-era streetscape"],
  },
  {
    slug: "manchester",
    name: "Manchester",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "hartford-county-ct",
    lat: 41.7759,
    lng: -72.5215,
    blurb:
      "Manchester's mix of village neighborhoods and wooded edges along Case Mountain gives us a wide range of installs, from tight dog runs to full deer exclusion.",
    lotProfile: "Quarter-acre village to two-acre wooded parcels.",
    deerPressure: "moderate",
    notableAttributes: ["Case Mountain trail edge", "Established neighborhoods"],
  },
  // ================= CONNECTICUT — Litchfield County (cont.) =================
  {
    slug: "cornwall",
    name: "Cornwall",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.8434,
    lng: -73.3315,
    blurb:
      "Cornwall's covered-bridge valley and hillside farms sit inside serious deer country. Our 8-foot near-invisible deer fence protects gardens and orchards.",
    lotProfile: "Multi-acre rural parcels with woodland edges.",
    deerPressure: "very-high",
    notableAttributes: ["Housatonic River valley", "Rural conservation ethic"],
  },
  {
    slug: "new-preston",
    name: "New Preston",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.6712,
    lng: -73.3462,
    blurb:
      "New Preston's Lake Waramaug estates and hillside cottages combine design-forward landscapes with real deer pressure. Our systems fade into the trees.",
    lotProfile: "Two- to ten-acre lakefront and hillside parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Lake Waramaug shoreline", "Design-forward second homes"],
  },
  {
    slug: "warren",
    name: "Warren",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.7295,
    lng: -73.3467,
    blurb:
      "Warren's open hilltop farms and quiet lake lots need fencing that respects the view. Near-invisible mesh and black metal keep sightlines clean.",
    lotProfile: "Multi-acre rural parcels, many with pasture and orchard.",
    deerPressure: "very-high",
    notableAttributes: ["Hilltop farm views", "Lake Waramaug proximity"],
  },
  // ================= CONNECTICUT — New Haven County Suburbs (cont.) =================
  {
    slug: "branford",
    name: "Branford",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "new-haven-county-suburbs-ct",
    lat: 41.2793,
    lng: -72.8151,
    blurb:
      "Branford's Stony Creek shoreline and inland wooded lots each carry distinct fence contexts. We tune to salt, deer, and lot shape.",
    lotProfile: "Coastal quarter-acre to inland two-acre parcels.",
    deerPressure: "moderate",
    notableAttributes: ["Thimble Islands shoreline", "Inland wooded parcels"],
  },
  {
    slug: "clinton",
    name: "Clinton",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "new-haven-county-suburbs-ct",
    lat: 41.2787,
    lng: -72.5292,
    blurb:
      "Clinton's shoreline cottages and inland woods each need different fence detailing. Salt-resistant hardware for the beach, near-invisible mesh for the trees.",
    lotProfile: "Quarter-acre coastal to one-acre inland parcels.",
    deerPressure: "moderate",
    notableAttributes: ["Long Island Sound shoreline", "Inland wooded edges"],
  },
  // ================= NEW YORK — Westchester (cont.) =================
  {
    slug: "mount-kisco",
    name: "Mount Kisco",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 41.2045,
    lng: -73.7290,
    blurb:
      "Mount Kisco's mix of village lots and wooded estates along Byram Lake sees heavy deer pressure. Our fencing performs on both scales.",
    lotProfile: "Half-acre village lots to five-acre wooded estates.",
    deerPressure: "very-high",
    notableAttributes: ["Byram Lake watershed", "Village and estate mix"],
  },
  {
    slug: "yorktown",
    name: "Yorktown",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "westchester-ny",
    lat: 41.2698,
    lng: -73.8137,
    blurb:
      "Yorktown Heights and Jefferson Valley sit deep in northern Westchester deer country. Full 8-foot exclusion is standard here.",
    lotProfile: "One- to five-acre wooded parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Croton watershed", "Heavy tree canopy"],
  },
  // ================= NEW YORK — Long Island (cont.) =================
  {
    slug: "manhasset",
    name: "Manhasset",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "long-island-ny",
    lat: 40.7970,
    lng: -73.7004,
    blurb:
      "Manhasset's Plandome and Munsey Park estates want fencing that reads as landscape, not enclosure. Near-invisible mesh and black metal integrate cleanly.",
    lotProfile: "Half- to two-acre established estate lots.",
    deerPressure: "moderate",
    notableAttributes: ["Plandome shoreline", "Established estate neighborhoods"],
  },
  {
    slug: "oyster-bay",
    name: "Oyster Bay",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "long-island-ny",
    lat: 40.8709,
    lng: -73.5318,
    blurb:
      "Oyster Bay's harbor estates and Cove Neck lots need fencing that handles salt and preserves views. We install to Gold Coast standards.",
    lotProfile: "One- to five-acre harbor and wooded parcels.",
    deerPressure: "moderate",
    notableAttributes: ["Historic Gold Coast estates", "Harbor exposure"],
  },
  {
    slug: "huntington",
    name: "Huntington",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "long-island-ny",
    lat: 40.8687,
    lng: -73.4257,
    blurb:
      "Huntington's Cold Spring Hills and Lloyd Neck estates carry real deer pressure. Our 8-foot mesh keeps gardens intact without turning yards into compounds.",
    lotProfile: "One- to four-acre wooded and harbor parcels.",
    deerPressure: "high",
    notableAttributes: ["Lloyd Neck estates", "Cold Spring Harbor edge"],
  },
  // ================= NEW YORK — The Hamptons (cont.) =================
  {
    slug: "sagaponack",
    name: "Sagaponack",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "the-hamptons-ny",
    lat: 40.9298,
    lng: -72.2673,
    blurb:
      "Sagaponack's farm-field estates and oceanfront parcels demand fencing that reads as agrarian, not suburban. Black metal and near-invisible mesh fit the landscape.",
    lotProfile: "Multi-acre farm-field and oceanfront parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Oceanfront and farmland", "Design review common"],
  },
  {
    slug: "amagansett",
    name: "Amagansett",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "the-hamptons-ny",
    lat: 40.9695,
    lng: -72.1443,
    blurb:
      "Amagansett's dune-front cottages and Lanes homes each ask for different fence details. We handle salt, sand, and heavy deer pressure alike.",
    lotProfile: "Half-acre village to multi-acre dune-front parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Dune-front exposure", "ARB review"],
  },
  {
    slug: "shelter-island",
    name: "Shelter Island",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "the-hamptons-ny",
    lat: 41.0662,
    lng: -72.3312,
    blurb:
      "Shelter Island's Ram Island and Dering Harbor properties sit inside one of the highest deer-density zones on the East Coast. Full exclusion is essential.",
    lotProfile: "One- to five-acre wooded and waterfront parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Extreme deer density", "Ferry-only access"],
  },
  // ================= NEW YORK — Hudson Valley (cont.) =================
  {
    slug: "red-hook",
    name: "Red Hook",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.9942,
    lng: -73.8779,
    blurb:
      "Red Hook's Tivoli-edge farms and orchard parcels sit in serious deer country. Our 8-foot mesh protects vegetable gardens and young trees.",
    lotProfile: "Multi-acre farm and orchard parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Orchard country", "Rural conservation ethic"],
  },
  {
    slug: "garrison",
    name: "Garrison",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.3812,
    lng: -73.9465,
    blurb:
      "Garrison's Hudson-facing estates and wooded hillsides need fencing that disappears against the river view while excluding heavy deer traffic.",
    lotProfile: "Multi-acre hillside estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Hudson River views", "Historic estate district"],
  },
  // ================= NEW YORK — Saratoga (cont.) =================
  {
    slug: "malta",
    name: "Malta",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "saratoga-county-ny",
    lat: 43.0142,
    lng: -73.7515,
    blurb:
      "Malta's Round Lake edge and newer estate builds sit close to Saratoga Springs deer corridors. We install year-round with frost-tolerant hardware.",
    lotProfile: "One- to three-acre suburban and wooded parcels.",
    deerPressure: "high",
    notableAttributes: ["Round Lake shoreline", "Deep-frost installs"],
  },
  // ================= NEW YORK — Columbia County (cont.) =================
  {
    slug: "hudson",
    name: "Hudson",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "columbia-county-ny",
    lat: 42.2528,
    lng: -73.7910,
    blurb:
      "Hudson's Warren Street townhouses and just-outside-town farmhouses need fencing that fits either the historic district or open-field context.",
    lotProfile: "Village lots to multi-acre farmhouse parcels.",
    deerPressure: "high",
    notableAttributes: ["Historic Warren Street", "Farmhouse renovations"],
  },
  // ================= NEW YORK — North Fork (cont.) =================
  {
    slug: "mattituck",
    name: "Mattituck",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 40.9987,
    lng: -72.5343,
    blurb:
      "Mattituck's vineyard-edge homes and creek-front parcels need fencing that respects working agriculture and heavy deer traffic alike.",
    lotProfile: "One- to five-acre vineyard-edge and creek parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Vineyard country", "Deer-heavy North Fork"],
  },
  {
    slug: "greenport",
    name: "Greenport",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 41.1037,
    lng: -72.3618,
    blurb:
      "Greenport's village lots and waterfront estates each need distinct fence detailing — salt-tolerant hardware for the harbor, near-invisible mesh for the trees.",
    lotProfile: "Village quarter-acre to multi-acre waterfront parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Harbor waterfront", "Historic village district"],
  },
  // ================= MASSACHUSETTS — MetroWest (cont.) =================
  {
    slug: "needham",
    name: "Needham",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.2809,
    lng: -71.2381,
    blurb:
      "Needham's established neighborhoods along Charles River Street and newer estate builds share consistent deer pressure and tight ARB review.",
    lotProfile: "Half- to two-acre suburban parcels.",
    deerPressure: "high",
    notableAttributes: ["Charles River edge", "Established neighborhoods"],
  },
  {
    slug: "wayland",
    name: "Wayland",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.3620,
    lng: -71.3617,
    blurb:
      "Wayland's Sudbury River corridor and wooded back roads carry serious deer pressure. Our 8-foot near-invisible mesh protects gardens and orchards.",
    lotProfile: "One- to three-acre wooded parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Sudbury River corridor", "Conservation-commission review"],
  },
  {
    slug: "natick",
    name: "Natick",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.2834,
    lng: -71.3495,
    blurb:
      "Natick's South Natick estates and inland woods each ask for different fence detailing. We tune to lot, canopy, and conservation-commission overlay.",
    lotProfile: "Half- to two-acre suburban and wooded parcels.",
    deerPressure: "high",
    notableAttributes: ["South Natick estates", "Charles River proximity"],
  },
  // ================= MASSACHUSETTS — Berkshires (cont.) =================
  {
    slug: "sheffield",
    name: "Sheffield",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "the-berkshires-ma",
    lat: 42.1148,
    lng: -73.3576,
    blurb:
      "Sheffield's Housatonic-valley farms and hillside second homes need fencing that reads agrarian while excluding heavy deer pressure.",
    lotProfile: "Multi-acre farm and hillside parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Housatonic valley farmland", "Second-home market"],
  },
  {
    slug: "egremont",
    name: "Egremont",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "the-berkshires-ma",
    lat: 42.1479,
    lng: -73.4237,
    blurb:
      "Egremont's Baldwin Hill farms and hillside cottages sit inside serious deer country. Near-invisible mesh fits the landscape.",
    lotProfile: "Multi-acre rural parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Baldwin Hill farmland", "Hillside second homes"],
  },
  // ================= MASSACHUSETTS — Cape Cod (cont.) =================
  {
    slug: "brewster",
    name: "Brewster",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-cod-ma",
    lat: 41.7601,
    lng: -70.0821,
    blurb:
      "Brewster's kettle-pond parcels and Cape Cod Bay-facing lots need fencing that respects conservation-commission review and heavy Cape deer traffic.",
    lotProfile: "Half- to two-acre wooded and pond-edge parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Kettle-pond neighborhoods", "Conservation-commission review"],
  },
  {
    slug: "truro",
    name: "Truro",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-cod-ma",
    lat: 41.9959,
    lng: -70.0498,
    blurb:
      "Truro's dune-front homes and back-shore woods sit in some of the heaviest deer pressure on the Cape. Full exclusion is standard here.",
    lotProfile: "One- to three-acre dune and wooded parcels.",
    deerPressure: "very-high",
    notableAttributes: ["National Seashore edge", "Dune-front exposure"],
  },
  // ================= MASSACHUSETTS — North Shore (cont.) =================
  {
    slug: "ipswich",
    name: "Ipswich",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.6792,
    lng: -70.8412,
    blurb:
      "Ipswich's Argilla Road estates and salt-marsh edges each need distinct fence detailing. Salt-tolerant hardware and near-invisible mesh fit the setting.",
    lotProfile: "Multi-acre coastal and inland parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Crane Estate area", "Salt-marsh edges"],
  },
  {
    slug: "topsfield",
    name: "Topsfield",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.6376,
    lng: -70.9495,
    blurb:
      "Topsfield's horse farms and open-field estates sit in heavy deer country. Our 8-foot mesh protects gardens, orchards, and paddock edges.",
    lotProfile: "Multi-acre horse-farm and estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Horse-farm country", "Open-field estates"],
  },
  // ================= MASSACHUSETTS — South Shore (cont.) =================
  {
    slug: "marshfield",
    name: "Marshfield",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "south-shore-ma",
    lat: 42.0917,
    lng: -70.7050,
    blurb:
      "Marshfield's Rexhame and Green Harbor cottages and inland woods each need distinct fence detailing — salt hardware for the beach, mesh for the trees.",
    lotProfile: "Coastal quarter-acre to inland two-acre parcels.",
    deerPressure: "high",
    notableAttributes: ["Coastal salt exposure", "Inland wooded edges"],
  },
  {
    slug: "plymouth",
    name: "Plymouth",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "south-shore-ma",
    lat: 41.9584,
    lng: -70.6673,
    blurb:
      "Plymouth's Manomet estates, Chiltonville historic homes, and Pine Hills neighborhoods each carry different review standards and deer pressure.",
    lotProfile: "Half- to five-acre wooded and coastal parcels.",
    deerPressure: "high",
    notableAttributes: ["Manomet coastal edge", "Pine Hills community"],
  },
  // ================= MASSACHUSETTS — Cape Ann (cont.) =================
  {
    slug: "gloucester",
    name: "Gloucester",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-ann-ma",
    lat: 42.6159,
    lng: -70.6620,
    blurb:
      "Gloucester's Eastern Point estates and back-shore neighborhoods each need distinct fence detailing. Salt-tolerant hardware is standard.",
    lotProfile: "Half- to three-acre coastal and inland parcels.",
    deerPressure: "high",
    notableAttributes: ["Eastern Point estates", "Working harbor town"],
  },
  {
    slug: "rockport",
    name: "Rockport",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-ann-ma",
    lat: 42.6559,
    lng: -70.6200,
    blurb:
      "Rockport's granite-ledge lots and Pigeon Cove parcels need fencing that handles salt, wind, and shallow soil. We install to those constraints.",
    lotProfile: "Quarter- to two-acre coastal and ledge parcels.",
    deerPressure: "high",
    notableAttributes: ["Granite ledge terrain", "Pigeon Cove neighborhood"],
  },
  // ================= CONNECTICUT — Hartford County (cont.) =================
  {
    slug: "south-windsor",
    name: "South Windsor",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "hartford-county-ct",
    lat: 41.8237,
    lng: -72.6223,
    blurb:
      "South Windsor's tobacco-shed farmland and newer estate builds each need distinct fence detailing. We tune to lot, canopy, and deer corridor.",
    lotProfile: "One- to five-acre suburban and open-field parcels.",
    deerPressure: "high",
    notableAttributes: ["Connecticut River valley", "Working farmland edge"],
  },
  // ================= CONNECTICUT — Litchfield County (cont.) =================
  {
    slug: "goshen",
    name: "Goshen",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.8340,
    lng: -73.2287,
    blurb:
      "Goshen's hilltop farms and Tyler Lake cottages sit in high deer country. Our 8-foot near-invisible mesh protects gardens and orchards.",
    lotProfile: "Multi-acre rural and lakefront parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Tyler Lake shoreline", "Hilltop farmland"],
  },
  {
    slug: "morris",
    name: "Morris",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.6870,
    lng: -73.1893,
    blurb:
      "Morris's Bantam Lake edge and wooded back roads carry serious deer pressure. Near-invisible mesh fits the landscape and holds the line.",
    lotProfile: "One- to five-acre lake-edge and wooded parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Bantam Lake shoreline", "Rural conservation ethic"],
  },
  {
    slug: "bridgewater",
    name: "Bridgewater",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "litchfield-county-ct",
    lat: 41.5342,
    lng: -73.3670,
    blurb:
      "Bridgewater's open-field farms and hillside estates need fencing that reads agrarian while excluding heavy deer traffic.",
    lotProfile: "Multi-acre farm and estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Working farmland", "Second-home market"],
  },
  // ================= CONNECTICUT — New Haven County Suburbs (cont.) =================
  {
    slug: "woodbridge",
    name: "Woodbridge",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "new-haven-county-suburbs-ct",
    lat: 41.3540,
    lng: -73.0079,
    blurb:
      "Woodbridge's wooded acre-plus lots along West Rock ridge carry consistent deer pressure. Our systems fade into the tree canopy.",
    lotProfile: "One- to three-acre wooded parcels.",
    deerPressure: "very-high",
    notableAttributes: ["West Rock ridge edge", "Heavy tree canopy"],
  },
  {
    slug: "bethany",
    name: "Bethany",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "new-haven-county-suburbs-ct",
    lat: 41.4245,
    lng: -73.0037,
    blurb:
      "Bethany's rural back roads and horse farms sit in serious deer country. Our 8-foot mesh protects paddock edges, orchards, and vegetable gardens.",
    lotProfile: "Multi-acre horse-farm and wooded parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Horse-farm country", "Rural residential"],
  },
  {
    slug: "orange",
    name: "Orange",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "new-haven-county-suburbs-ct",
    lat: 41.2787,
    lng: -73.0248,
    blurb:
      "Orange's established suburban neighborhoods and inland woods each ask for distinct fence detailing. We tune to lot and canopy.",
    lotProfile: "Half- to two-acre suburban parcels.",
    deerPressure: "high",
    notableAttributes: ["Established suburban core", "Inland wooded edges"],
  },
  {
    slug: "north-branford",
    name: "North Branford",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "new-haven-county-suburbs-ct",
    lat: 41.3273,
    lng: -72.7695,
    blurb:
      "North Branford's Northford village and open-field parcels see consistent deer traffic from adjacent watershed land. Our fencing holds the line.",
    lotProfile: "One- to three-acre wooded and open-field parcels.",
    deerPressure: "high",
    notableAttributes: ["Watershed-adjacent parcels", "Village and rural mix"],
  },
  {
    slug: "killingworth",
    name: "Killingworth",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "new-haven-county-suburbs-ct",
    lat: 41.3618,
    lng: -72.5762,
    blurb:
      "Killingworth's wooded back roads and Cockaponset State Forest edges deliver some of the heaviest deer pressure in the shoreline suburbs.",
    lotProfile: "Multi-acre wooded parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Cockaponset State Forest edge", "Rural residential"],
  },
  {
    slug: "westbrook",
    name: "Westbrook",
    state: "CT",
    stateName: "Connecticut",
    stateSlug: "connecticut",
    parentRegionSlug: "new-haven-county-suburbs-ct",
    lat: 41.2837,
    lng: -72.4443,
    blurb:
      "Westbrook's shoreline cottages and inland woods each need different fence detailing. Salt-tolerant hardware for the beach, near-invisible mesh inland.",
    lotProfile: "Quarter-acre coastal to two-acre inland parcels.",
    deerPressure: "moderate",
    notableAttributes: ["Long Island Sound shoreline", "Inland wooded edges"],
  },
  // ================= NEW YORK — Long Island (cont.) =================
  {
    slug: "sands-point",
    name: "Sands Point",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "long-island-ny",
    lat: 40.8515,
    lng: -73.7290,
    blurb:
      "Sands Point's Gold Coast estates want fencing that stays quiet against manicured landscapes while handling harbor salt and heavy deer traffic.",
    lotProfile: "Multi-acre waterfront and estate parcels.",
    deerPressure: "high",
    notableAttributes: ["Gold Coast estate district", "Harbor exposure"],
  },
  {
    slug: "old-westbury",
    name: "Old Westbury",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "long-island-ny",
    lat: 40.7873,
    lng: -73.5987,
    blurb:
      "Old Westbury's estate parcels and horse farms need fencing that respects mature landscapes and reads as agrarian, not enclosure.",
    lotProfile: "Multi-acre estate and horse-farm parcels.",
    deerPressure: "moderate",
    notableAttributes: ["Historic estate district", "Horse-farm country"],
  },
  {
    slug: "locust-valley",
    name: "Locust Valley",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "long-island-ny",
    lat: 40.8770,
    lng: -73.5987,
    blurb:
      "Locust Valley's wooded estate parcels and horse country sit inside heavy deer pressure. Our 8-foot mesh keeps gardens and paddocks intact.",
    lotProfile: "Multi-acre wooded and estate parcels.",
    deerPressure: "high",
    notableAttributes: ["Gold Coast estate district", "Horse-farm country"],
  },
  {
    slug: "cold-spring-harbor",
    name: "Cold Spring Harbor",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "long-island-ny",
    lat: 40.8720,
    lng: -73.4593,
    blurb:
      "Cold Spring Harbor's hillside estates overlooking the harbor need fencing that preserves the view while excluding heavy deer traffic.",
    lotProfile: "One- to five-acre hillside and waterfront parcels.",
    deerPressure: "high",
    notableAttributes: ["Harbor views", "Wooded hillsides"],
  },
  {
    slug: "lloyd-harbor",
    name: "Lloyd Harbor",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "long-island-ny",
    lat: 40.9101,
    lng: -73.4568,
    blurb:
      "Lloyd Harbor's peninsula estates carry serious deer pressure and salt exposure. We spec hardware for both and integrate fencing into the landscape.",
    lotProfile: "Multi-acre waterfront and wooded estate parcels.",
    deerPressure: "high",
    notableAttributes: ["Peninsula waterfront", "Estate-scale perimeters"],
  },
  // ================= NEW YORK — Hamptons (cont.) =================
  {
    slug: "wainscott",
    name: "Wainscott",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "the-hamptons-ny",
    lat: 40.9401,
    lng: -72.2437,
    blurb:
      "Wainscott's oceanfront estates and reserve-edge parcels sit in some of the heaviest deer country on the East End. Full 8-foot exclusion is standard.",
    lotProfile: "Multi-acre oceanfront and reserve-adjacent parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Oceanfront exposure", "Reserve-adjacent lots"],
  },
  {
    slug: "quogue",
    name: "Quogue",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "the-hamptons-ny",
    lat: 40.8148,
    lng: -72.6098,
    blurb:
      "Quogue's village estates and Dune Road oceanfront parcels each need distinct fence detailing — salt hardware for the dunes, near-invisible mesh inland.",
    lotProfile: "One- to five-acre village and oceanfront parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Village historic district", "Dune Road oceanfront"],
  },
  {
    slug: "north-haven",
    name: "North Haven",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "the-hamptons-ny",
    lat: 41.0087,
    lng: -72.3018,
    blurb:
      "North Haven's peninsula estates carry very high deer pressure. Our near-invisible mesh integrates into wooded lots without breaking the view.",
    lotProfile: "One- to five-acre waterfront and wooded parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Peninsula waterfront", "Heavy deer density"],
  },
  // ================= NEW YORK — Hudson Valley (cont.) =================
  {
    slug: "pawling",
    name: "Pawling",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.5637,
    lng: -73.6018,
    blurb:
      "Pawling's Quaker Hill estates and hillside farms sit in serious deer country. Our 8-foot mesh protects gardens, orchards, and young trees.",
    lotProfile: "Multi-acre estate and hillside farm parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Quaker Hill estates", "Rural hillside farms"],
  },
  {
    slug: "amenia",
    name: "Amenia",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.8515,
    lng: -73.5551,
    blurb:
      "Amenia's Silo Ridge and Troutbeck estates and open-field farms need fencing that reads agrarian while excluding heavy deer traffic.",
    lotProfile: "Multi-acre farm and estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Silo Ridge community", "Working farmland"],
  },
  // ================= NEW YORK — Saratoga (cont.) =================
  {
    slug: "saratoga",
    name: "Saratoga",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "saratoga-county-ny",
    lat: 43.0770,
    lng: -73.7290,
    blurb:
      "Saratoga's horse-country estates and lake-edge parcels carry heavy deer pressure and long frost seasons. We install year-round with frost-tolerant hardware.",
    lotProfile: "Multi-acre horse-farm and estate parcels.",
    deerPressure: "high",
    notableAttributes: ["Horse-farm country", "Deep-frost installs"],
  },
  {
    slug: "stillwater",
    name: "Stillwater",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "saratoga-county-ny",
    lat: 42.9414,
    lng: -73.6540,
    blurb:
      "Stillwater's Hudson-edge farms and Saratoga Lake cottages each need distinct fence detailing. We tune to lot, canopy, and deer corridor.",
    lotProfile: "One- to five-acre farm and lake-edge parcels.",
    deerPressure: "high",
    notableAttributes: ["Hudson River edge", "Saratoga Lake shoreline"],
  },
  // ================= NEW YORK — Columbia County (cont.) =================
  {
    slug: "kinderhook",
    name: "Kinderhook",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "columbia-county-ny",
    lat: 42.3970,
    lng: -73.6976,
    blurb:
      "Kinderhook's historic village and surrounding farmland carry heavy deer pressure. Near-invisible mesh reads as landscape, not enclosure.",
    lotProfile: "Village lots to multi-acre farm parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Historic village district", "Working farmland"],
  },
  {
    slug: "hillsdale",
    name: "Hillsdale",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "columbia-county-ny",
    lat: 42.2087,
    lng: -73.5265,
    blurb:
      "Hillsdale's Catamount-adjacent estates and Roe Jan valley farms sit in serious deer country. Full 8-foot exclusion protects gardens and orchards.",
    lotProfile: "Multi-acre farm and hillside estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Roe Jan valley", "Catamount ski area edge"],
  },
  // ================= NEW YORK — North Fork (cont.) =================
  {
    slug: "orient",
    name: "Orient",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 41.1443,
    lng: -72.3062,
    blurb:
      "Orient's farmhouse parcels and Orient Point estates sit at the very end of the North Fork under extreme deer pressure. Full exclusion is standard.",
    lotProfile: "One- to five-acre farm and waterfront parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Orient Point waterfront", "Working farmland"],
  },
  {
    slug: "riverhead",
    name: "Riverhead",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 40.9170,
    lng: -72.6620,
    blurb:
      "Riverhead's farmland edges and Peconic-facing estates each need distinct fence detailing. We tune to lot, crop, and deer corridor.",
    lotProfile: "One- to ten-acre farm and waterfront parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Peconic Bay edge", "Working farmland"],
  },
  // ================= MASSACHUSETTS — MetroWest (cont.) =================
  {
    slug: "westwood",
    name: "Westwood",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "metrowest-ma",
    lat: 42.2137,
    lng: -71.2245,
    blurb:
      "Westwood's established estate lots and wooded back roads carry consistent deer pressure. Our systems disappear into mature landscaping.",
    lotProfile: "One- to three-acre estate parcels.",
    deerPressure: "high",
    notableAttributes: ["Established estate district", "Mature tree canopy"],
  },
  // ================= MASSACHUSETTS — Berkshires (cont.) =================
  {
    slug: "tyringham",
    name: "Tyringham",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "the-berkshires-ma",
    lat: 42.2470,
    lng: -73.1926,
    blurb:
      "Tyringham's valley farms and hillside second homes sit deep in Berkshires deer country. Near-invisible mesh integrates into the landscape.",
    lotProfile: "Multi-acre rural parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Tyringham Valley", "Second-home market"],
  },
  {
    slug: "richmond",
    name: "Richmond",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "the-berkshires-ma",
    lat: 42.3762,
    lng: -73.3620,
    blurb:
      "Richmond's hillside farms and pond-edge homes carry serious deer pressure. Our 8-foot mesh protects gardens, orchards, and young trees.",
    lotProfile: "Multi-acre farm and pond-edge parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Richmond Pond shoreline", "Hillside farms"],
  },
  // ================= MASSACHUSETTS — Cape Cod (cont.) =================
  {
    slug: "eastham",
    name: "Eastham",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-cod-ma",
    lat: 41.8301,
    lng: -69.9737,
    blurb:
      "Eastham's National Seashore edges and kettle-pond lots sit in serious Cape deer country. Full exclusion protects gardens and young trees.",
    lotProfile: "Half- to two-acre wooded and pond-edge parcels.",
    deerPressure: "very-high",
    notableAttributes: ["National Seashore edge", "Kettle-pond neighborhoods"],
  },
  {
    slug: "dennis",
    name: "Dennis",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-cod-ma",
    lat: 41.7359,
    lng: -70.1937,
    blurb:
      "Dennis's bay-side cottages and inland woods each need distinct fence detailing. Salt hardware for the beach, near-invisible mesh for the trees.",
    lotProfile: "Quarter-acre coastal to two-acre inland parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Cape Cod Bay shoreline", "Kettle-pond neighborhoods"],
  },
  {
    slug: "harwich",
    name: "Harwich",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-cod-ma",
    lat: 41.6890,
    lng: -70.0776,
    blurb:
      "Harwich's cranberry-bog neighborhoods and Nantucket Sound coastal lots each need distinct fence detailing. We tune to lot and canopy.",
    lotProfile: "Half- to two-acre coastal and bog-edge parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Cranberry-bog country", "Nantucket Sound shoreline"],
  },
  // ================= MASSACHUSETTS — North Shore (cont.) =================
  {
    slug: "hamilton",
    name: "Hamilton",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.6303,
    lng: -70.8551,
    blurb:
      "Hamilton's horse-country estates and open-field farms sit in serious deer country. Our 8-foot mesh protects gardens, orchards, and paddock edges.",
    lotProfile: "Multi-acre horse-farm and estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Myopia Hunt country", "Horse-farm estates"],
  },
  {
    slug: "wenham",
    name: "Wenham",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.6009,
    lng: -70.8776,
    blurb:
      "Wenham's estate lots and horse-country back roads carry very high deer pressure. Near-invisible mesh reads as landscape, not enclosure.",
    lotProfile: "Multi-acre estate and horse-farm parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Horse-farm country", "Historic estate district"],
  },
  {
    slug: "boxford",
    name: "Boxford",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.6812,
    lng: -71.0176,
    blurb:
      "Boxford's wooded acre-plus lots along state forest edges sit in serious deer country. Full 8-foot exclusion is standard here.",
    lotProfile: "Multi-acre wooded parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Boxford State Forest edge", "Heavy tree canopy"],
  },
  {
    slug: "essex",
    name: "Essex",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.6320,
    lng: -70.7828,
    blurb:
      "Essex's salt-marsh edges and wooded inland lots each need distinct fence detailing. Salt-tolerant hardware and near-invisible mesh fit the setting.",
    lotProfile: "Half- to two-acre coastal and inland parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Great Marsh edge", "Wooded inland lots"],
  },
  // ================= MASSACHUSETTS — South Shore (cont.) =================
  {
    slug: "norwell",
    name: "Norwell",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "south-shore-ma",
    lat: 42.1620,
    lng: -70.7940,
    blurb:
      "Norwell's wooded acre-plus lots and North River corridor properties carry consistent deer pressure. Our fencing fades into the tree canopy.",
    lotProfile: "One- to three-acre wooded parcels.",
    deerPressure: "high",
    notableAttributes: ["North River corridor", "Heavy tree canopy"],
  },
  {
    slug: "hanover",
    name: "Hanover",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "south-shore-ma",
    lat: 42.1148,
    lng: -70.8123,
    blurb:
      "Hanover's established neighborhoods and inland woods each ask for different fence detailing. We tune to lot and canopy.",
    lotProfile: "Half- to two-acre suburban and wooded parcels.",
    deerPressure: "high",
    notableAttributes: ["Established neighborhoods", "Inland wooded edges"],
  },
  {
    slug: "pembroke",
    name: "Pembroke",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "south-shore-ma",
    lat: 42.0687,
    lng: -70.7940,
    blurb:
      "Pembroke's pond-edge neighborhoods and inland woods sit in high deer country. Our 8-foot mesh protects gardens and vegetable beds.",
    lotProfile: "Half- to two-acre wooded and pond-edge parcels.",
    deerPressure: "high",
    notableAttributes: ["Great Sandy Bottom Pond edge", "Wooded inland lots"],
  },
  {
    slug: "milton",
    name: "Milton",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "south-shore-ma",
    lat: 42.2495,
    lng: -71.0662,
    blurb:
      "Milton's Blue Hills-edge estates and established neighborhoods along Brush Hill carry consistent deer pressure. Our systems disappear into mature landscaping.",
    lotProfile: "Half- to three-acre estate and suburban parcels.",
    deerPressure: "high",
    notableAttributes: ["Blue Hills Reservation edge", "Established estate district"],
  },
  // ================= NEW YORK — Long Island (cont.) =================
  {
    slug: "brookville",
    name: "Brookville",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "long-island-ny",
    lat: 40.8237,
    lng: -73.5762,
    blurb:
      "Brookville's Gold Coast estates and wooded acre-plus parcels carry consistent deer pressure. Near-invisible mesh reads as landscape, not enclosure.",
    lotProfile: "Multi-acre estate parcels.",
    deerPressure: "high",
    notableAttributes: ["Gold Coast estate district", "Heavy tree canopy"],
  },
  {
    slug: "centre-island",
    name: "Centre Island",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "long-island-ny",
    lat: 40.9098,
    lng: -73.5157,
    blurb:
      "Centre Island's peninsula estates carry heavy salt exposure and steady deer traffic. We spec hardware for both and integrate fencing into the landscape.",
    lotProfile: "Multi-acre waterfront estate parcels.",
    deerPressure: "high",
    notableAttributes: ["Peninsula waterfront", "Salt exposure"],
  },
  {
    slug: "mill-neck",
    name: "Mill Neck",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "long-island-ny",
    lat: 40.8898,
    lng: -73.5551,
    blurb:
      "Mill Neck's wooded estate parcels and harbor-edge lots need fencing that respects mature landscaping while excluding heavy deer traffic.",
    lotProfile: "Multi-acre wooded and waterfront estate parcels.",
    deerPressure: "high",
    notableAttributes: ["Gold Coast estate district", "Harbor edges"],
  },
  // ================= NEW YORK — Hudson Valley (cont.) =================
  {
    slug: "pine-plains",
    name: "Pine Plains",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.9770,
    lng: -73.6607,
    blurb:
      "Pine Plains's open-field farms and Stissing-edge estates sit in serious deer country. Our 8-foot mesh protects gardens, orchards, and young trees.",
    lotProfile: "Multi-acre farm and hillside parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Stissing Mountain edge", "Working farmland"],
  },
  {
    slug: "stanfordville",
    name: "Stanfordville",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.8873,
    lng: -73.6773,
    blurb:
      "Stanfordville's hamlet lots and rolling farmland carry heavy deer pressure. Near-invisible mesh fades into the tree line while holding the boundary.",
    lotProfile: "Multi-acre farm and rural parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Rural farmland", "Second-home market"],
  },
  {
    slug: "salt-point",
    name: "Salt Point",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "hudson-valley-ny",
    lat: 41.8020,
    lng: -73.7940,
    blurb:
      "Salt Point's wooded acre-plus lots and small farms sit adjacent to state land with heavy deer traffic. Full exclusion is standard.",
    lotProfile: "One- to five-acre wooded and farm parcels.",
    deerPressure: "very-high",
    notableAttributes: ["State-land-adjacent parcels", "Wooded back roads"],
  },
  // ================= NEW YORK — Saratoga (cont.) =================
  {
    slug: "greenfield",
    name: "Greenfield",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "saratoga-county-ny",
    lat: 43.1148,
    lng: -73.8368,
    blurb:
      "Greenfield's rural hillside farms and second-home parcels carry heavy deer pressure and long frost seasons. We install with frost-tolerant hardware.",
    lotProfile: "Multi-acre rural parcels.",
    deerPressure: "high",
    notableAttributes: ["Rural hillside farms", "Deep-frost installs"],
  },
  {
    slug: "charlton",
    name: "Charlton",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "saratoga-county-ny",
    lat: 42.9648,
    lng: -73.9748,
    blurb:
      "Charlton's rolling farmland and horse-country parcels sit in serious deer country. Our 8-foot mesh protects paddock edges and vegetable gardens.",
    lotProfile: "Multi-acre farm and horse-country parcels.",
    deerPressure: "high",
    notableAttributes: ["Horse-farm country", "Working farmland"],
  },
  {
    slug: "galway",
    name: "Galway",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "saratoga-county-ny",
    lat: 43.0359,
    lng: -74.0193,
    blurb:
      "Galway's lake-edge homes and hillside farms carry heavy deer pressure. Near-invisible mesh integrates into the landscape while excluding deer.",
    lotProfile: "One- to five-acre lake-edge and farm parcels.",
    deerPressure: "high",
    notableAttributes: ["Galway Lake shoreline", "Rural farmland"],
  },
  // ================= NEW YORK — Columbia County (cont.) =================
  {
    slug: "ghent",
    name: "Ghent",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "columbia-county-ny",
    lat: 42.3384,
    lng: -73.6112,
    blurb:
      "Ghent's rolling farmland and second-home estates sit deep in Columbia County deer country. Full 8-foot exclusion protects gardens and orchards.",
    lotProfile: "Multi-acre farm and estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Working farmland", "Second-home market"],
  },
  {
    slug: "claverack",
    name: "Claverack",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "columbia-county-ny",
    lat: 42.2273,
    lng: -73.7290,
    blurb:
      "Claverack's Hudson-edge farmhouses and open-field parcels need fencing that reads agrarian while excluding heavy deer traffic.",
    lotProfile: "Multi-acre farm and rural parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Working farmland", "Historic farmhouses"],
  },
  {
    slug: "ancram",
    name: "Ancram",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "columbia-county-ny",
    lat: 42.0570,
    lng: -73.6373,
    blurb:
      "Ancram's rural back roads and hillside farms sit in some of the heaviest deer pressure in Columbia County. Full exclusion is standard.",
    lotProfile: "Multi-acre farm and rural parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Hillside farmland", "Rural conservation ethic"],
  },
  {
    slug: "copake",
    name: "Copake",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "columbia-county-ny",
    lat: 42.1179,
    lng: -73.5401,
    blurb:
      "Copake's Taconic-edge parcels and lake-front homes carry very high deer pressure. Our 8-foot mesh keeps gardens and vegetable beds intact.",
    lotProfile: "Multi-acre wooded and lake-edge parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Copake Lake shoreline", "Taconic State Park edge"],
  },
  {
    slug: "livingston",
    name: "Livingston",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "columbia-county-ny",
    lat: 42.1531,
    lng: -73.7784,
    blurb:
      "Livingston's rolling farmland and hillside estates sit in serious deer country. Near-invisible mesh integrates into the landscape without breaking views.",
    lotProfile: "Multi-acre farm and estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Working farmland", "Hillside estate parcels"],
  },
  {
    slug: "old-chatham",
    name: "Old Chatham",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "columbia-county-ny",
    lat: 42.4620,
    lng: -73.5648,
    blurb:
      "Old Chatham's Shaker-country farmland and hillside estates carry heavy deer pressure. Our fencing reads agrarian while excluding deer effectively.",
    lotProfile: "Multi-acre farm and estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Shaker Museum area", "Working farmland"],
  },
  // ================= NEW YORK — North Fork (cont.) =================
  {
    slug: "jamesport",
    name: "Jamesport",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 40.9448,
    lng: -72.5787,
    blurb:
      "Jamesport's vineyard-edge homes and bay-facing lots sit in heavy North Fork deer country. Full 8-foot exclusion protects gardens and young vines.",
    lotProfile: "One- to five-acre vineyard-edge and waterfront parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Vineyard country", "Peconic Bay edge"],
  },
  {
    slug: "new-suffolk",
    name: "New Suffolk",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 40.9998,
    lng: -72.4737,
    blurb:
      "New Suffolk's tiny waterfront hamlet sits under extreme deer pressure. Near-invisible mesh keeps gardens intact against constant browse.",
    lotProfile: "Half- to two-acre waterfront and village parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Peconic Bay hamlet", "Historic waterfront"],
  },
  {
    slug: "peconic",
    name: "Peconic",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 41.0498,
    lng: -72.4593,
    blurb:
      "Peconic's vineyard rows and waterfront homes sit deep in North Fork deer country. Our 8-foot mesh integrates into vineyard hedgerows.",
    lotProfile: "One- to ten-acre vineyard and waterfront parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Vineyard country", "Peconic Bay waterfront"],
  },
  {
    slug: "laurel",
    name: "Laurel",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 40.9573,
    lng: -72.5620,
    blurb:
      "Laurel's small-farm parcels and creek-front homes carry heavy deer pressure. Full exclusion protects vegetable gardens and orchards.",
    lotProfile: "One- to three-acre farm and creek-front parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Creek-front parcels", "Small farms"],
  },
  {
    slug: "east-marion",
    name: "East Marion",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 41.1287,
    lng: -72.3462,
    blurb:
      "East Marion's bay-facing lots and vineyard-edge parcels sit near the tip of the North Fork under extreme deer pressure. Full exclusion is essential.",
    lotProfile: "One- to five-acre vineyard and waterfront parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Vineyard edges", "Bay-facing waterfront"],
  },
  {
    slug: "aquebogue",
    name: "Aquebogue",
    state: "NY",
    stateName: "New York",
    stateSlug: "new-york",
    parentRegionSlug: "north-fork-long-island-ny",
    lat: 40.9401,
    lng: -72.6146,
    blurb:
      "Aquebogue's working farms and vineyard rows sit at the western edge of the North Fork deer corridor. Our 8-foot mesh protects crops and gardens.",
    lotProfile: "One- to ten-acre farm and vineyard parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Working farmland", "Vineyard country"],
  },
  // ================= MASSACHUSETTS — North Shore (cont.) =================
  {
    slug: "beverly-farms",
    name: "Beverly Farms",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.5620,
    lng: -70.8112,
    blurb:
      "Beverly Farms's shoreline estates and wooded acre-plus parcels each need distinct fence detailing. Salt-tolerant hardware and near-invisible mesh fit the setting.",
    lotProfile: "One- to five-acre coastal and wooded estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Historic estate district", "Coastal salt exposure"],
  },
  {
    slug: "prides-crossing",
    name: "Prides Crossing",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.5570,
    lng: -70.8298,
    blurb:
      "Prides Crossing's estate lots and mature landscapes carry consistent deer pressure. Near-invisible mesh disappears into hedgerows and stone walls.",
    lotProfile: "Multi-acre estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Historic estate district", "Mature landscaping"],
  },
  {
    slug: "magnolia",
    name: "Magnolia",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.5820,
    lng: -70.7248,
    blurb:
      "Magnolia's clifftop cottages and wooded back roads each need distinct fence detailing. Salt-resistant hardware for the coast, near-invisible mesh inland.",
    lotProfile: "Quarter- to two-acre coastal and wooded parcels.",
    deerPressure: "high",
    notableAttributes: ["Coastal clifftops", "Wooded back roads"],
  },
  {
    slug: "south-hamilton",
    name: "South Hamilton",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.6120,
    lng: -70.8637,
    blurb:
      "South Hamilton's horse-country estates and open-field farms sit in serious deer country. Our 8-foot mesh protects paddock edges and gardens.",
    lotProfile: "Multi-acre horse-farm and estate parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Myopia Hunt country", "Horse-farm estates"],
  },
  {
    slug: "rowley",
    name: "Rowley",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "north-shore-ma",
    lat: 42.7218,
    lng: -70.8776,
    blurb:
      "Rowley's salt-marsh edges and inland farmland each need distinct fence detailing. We tune to lot, crop, and heavy deer traffic.",
    lotProfile: "One- to five-acre coastal and farm parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Great Marsh edge", "Working farmland"],
  },
  // ================= MASSACHUSETTS — Cape Cod (cont.) =================
  {
    slug: "yarmouth-port",
    name: "Yarmouth Port",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-cod-ma",
    lat: 41.7059,
    lng: -70.2312,
    blurb:
      "Yarmouth Port's historic Old King's Highway homes and bay-side lots each need distinct fence detailing that clears historic-district review.",
    lotProfile: "Half- to two-acre historic and bay-side parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Old King's Highway historic district", "Cape Cod Bay edge"],
  },
  // ================= MASSACHUSETTS — Cape Ann (cont.) =================
  {
    slug: "annisquam",
    name: "Annisquam",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-ann-ma",
    lat: 42.6570,
    lng: -70.6812,
    blurb:
      "Annisquam's harbor-village cottages and rocky waterfront parcels need salt-tolerant hardware and fencing that fits the tight historic streetscape.",
    lotProfile: "Quarter- to one-acre village and waterfront parcels.",
    deerPressure: "high",
    notableAttributes: ["Historic harbor village", "Rocky waterfront"],
  },
  {
    slug: "lanesville",
    name: "Lanesville",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-ann-ma",
    lat: 42.6698,
    lng: -70.6540,
    blurb:
      "Lanesville's granite-quarry neighborhoods and coastal lots need fencing that handles shallow soil, ledge, and salt spray. We install to those constraints.",
    lotProfile: "Quarter- to one-acre ledge and coastal parcels.",
    deerPressure: "high",
    notableAttributes: ["Granite quarry heritage", "Rocky coastline"],
  },
  {
    slug: "east-gloucester",
    name: "East Gloucester",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-ann-ma",
    lat: 42.6015,
    lng: -70.6540,
    blurb:
      "East Gloucester's harbor-facing homes and Rocky Neck edges need salt-tolerant hardware and fencing that reads as landscape, not enclosure.",
    lotProfile: "Quarter- to one-acre coastal and village parcels.",
    deerPressure: "high",
    notableAttributes: ["Rocky Neck art colony", "Harbor exposure"],
  },
  {
    slug: "west-gloucester",
    name: "West Gloucester",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-ann-ma",
    lat: 42.6187,
    lng: -70.7245,
    blurb:
      "West Gloucester's wooded acre-plus lots and salt-marsh edges each need distinct fence detailing. We tune to lot, canopy, and heavy deer traffic.",
    lotProfile: "One- to three-acre wooded and marsh-edge parcels.",
    deerPressure: "very-high",
    notableAttributes: ["Salt-marsh edges", "Wooded back roads"],
  },
  {
    slug: "pigeon-cove",
    name: "Pigeon Cove",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-ann-ma",
    lat: 42.6812,
    lng: -70.6112,
    blurb:
      "Pigeon Cove's rocky coastline and granite-ledge lots need fencing that handles shallow soil and constant salt exposure. We spec hardware accordingly.",
    lotProfile: "Quarter- to one-acre ledge and coastal parcels.",
    deerPressure: "high",
    notableAttributes: ["Rocky coastline", "Granite ledge terrain"],
  },
  {
    slug: "bay-view",
    name: "Bay View",
    state: "MA",
    stateName: "Massachusetts",
    stateSlug: "massachusetts",
    parentRegionSlug: "cape-ann-ma",
    lat: 42.6420,
    lng: -70.6820,
    blurb:
      "Bay View's Ipswich Bay-facing lots and quiet cove neighborhoods each need distinct fence detailing. Salt-tolerant hardware is standard.",
    lotProfile: "Quarter- to one-acre coastal parcels.",
    deerPressure: "high",
    notableAttributes: ["Ipswich Bay exposure", "Quiet cove neighborhoods"],
  },
];

const townBySlug = new Map<string, TownData>();
const townByStateAndSlug = new Map<string, TownData>();
const townsByRegion = new Map<string, TownData[]>();
const townsByState = new Map<string, TownData[]>();

for (const t of towns) {
  townBySlug.set(t.slug, t);
  townByStateAndSlug.set(`${t.stateSlug}/${t.slug}`, t);
  const rArr = townsByRegion.get(t.parentRegionSlug) ?? [];
  rArr.push(t);
  townsByRegion.set(t.parentRegionSlug, rArr);
  const sArr = townsByState.get(t.stateSlug) ?? [];
  sArr.push(t);
  townsByState.set(t.stateSlug, sArr);
}

export function getTown(stateSlug: string, townSlug: string): TownData | undefined {
  return townByStateAndSlug.get(`${stateSlug}/${townSlug}`);
}

export function getStateHub(slug: string): StateHubData | undefined {
  return stateHubs.find((s) => s.slug === slug);
}

export function getTownsInRegion(regionSlug: string): TownData[] {
  return townsByRegion.get(regionSlug) ?? [];
}

export function getTownsInState(stateSlug: string): TownData[] {
  return townsByState.get(stateSlug) ?? [];
}

export function getRegionsInState(stateSlug: string): LocationData[] {
  const st = stateHubs.find((s) => s.slug === stateSlug);
  if (!st) return [];
  const townsHere = getTownsInState(stateSlug);
  const parentSlugs = Array.from(new Set(townsHere.map((t) => t.parentRegionSlug)));
  return parentSlugs
    .map((s) => getLocationBySlug(s))
    .filter((l): l is LocationData => Boolean(l));
}

/** Resolve a town by free-text area name (used by nearby-area cross-linking). */
const townByName = new Map<string, TownData>();
for (const t of towns) {
  townByName.set(t.name.toLowerCase(), t);
}
export function resolveTownByName(name: string): TownData | null {
  return townByName.get(name.toLowerCase().trim()) ?? null;
}
